import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, Navigate, useRoutes } from "react-router-dom";
import Box from "@mui/material/Box";
import { AuthLayout } from "layouts/auth";
import { DashboardLayout } from "layouts/dashboard";
import useAuthStore from "store/useAuthStore";
import ProtectedRoute from "components/protectedRoute";
import { CircularProgress } from "@mui/material";

// Lazy loading pages
export const HomePage = lazy(() => import("pages/home"));
export const AppLayoutPage = lazy(() => import("pages/layout/layout"));
export const UserPage = lazy(() => import("pages/user"));
export const CustomerPage = lazy(() => import("pages/customers/customer"));
export const CustomerDetailsPage = lazy(
  () => import("pages/customers/customerDetails")
);
export const RequestPage = lazy(() => import("pages/request"));
export const ServicesPage = lazy(() => import("pages/services/services"));
export const ServicesCategoriesPage = lazy(
  () => import("pages/services/categories")
);
export const UseCasePage = lazy(() => import("pages/usecases"));
export const SlidersPage = lazy(() => import("pages/sliders"));
export const ReportsPage = lazy(() => import("pages/reports"));
export const WheelPage = lazy(() => import("pages/wheel/wheel"));
export const PaymentsPage = lazy(() => import("pages/payments/payments"));
// export const WithdrawalsPage = lazy(() => import("pages/payments/withdrawals"));
export const WheelSpinsPage = lazy(() => import("pages/wheel/spins"));
export const PrizesPage = lazy(() => import("pages/prizes"));
export const CouponsPage = lazy(() => import("pages/coupons/coupons"));
export const ScorePage = lazy(() => import("pages/score"));
export const PlansPage = lazy(() => import("pages/plans/plans"));
export const ConversionsPage = lazy(() => import("pages/score/conversions"));
export const ClicksPage = lazy(() => import("pages/clicks"));
export const FaqPage = lazy(() => import("pages/faq"));
export const GamesPage = lazy(() => import("pages/games/games"));
export const SignInPage = lazy(() => import("pages/login/sign-in"));
export const Page404 = lazy(() => import("pages/page-not-found"));

export const NotificationPage = lazy(
  () => import("pages/notification/notification")
);

// Fallback while loading
const renderFallback = (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    flex="1 1 auto"
    sx={{
      marginTop: 8,
    }}
  >
    <CircularProgress />
  </Box>
);

export function Router() {
  const { checkAuth, isAuthenticated } = useAuthStore();

  // Track the loading state for auth check
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      await checkAuth(); // Assuming checkAuth() is async
      setLoading(false); // Once the auth check is done, update the loading state
    };

    checkAuthStatus();
  }, [checkAuth]);

  // Define routes (unconditionally)
  const routes = [
    {
      element: (
        // isAuthenticated ?
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={renderFallback}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
        // ) : (
        //   <Navigate to="/sign-in" replace />
      ),
      children: [
        { element: <HomePage />, index: true },
        { path: "user", element: <UserPage /> },
        { path: "layout", element: <AppLayoutPage /> },
        { path: "customer", element: <CustomerPage /> },
        { path: "customer/:id", element: <CustomerDetailsPage /> },
        { path: "request", element: <RequestPage /> },
        { path: "clicks", element: <ClicksPage /> },
        { path: "services", element: <ServicesPage /> },
        { path: "services/categories", element: <ServicesCategoriesPage /> },
        { path: "usecases", element: <UseCasePage /> },
        { path: "sliders", element: <SlidersPage /> },
        { path: "reports", element: <ReportsPage /> },
        { path: "coupons", element: <CouponsPage /> },
        { path: "wheel", element: <WheelPage /> },
        { path: "payments", element: <PaymentsPage /> },
        // { path: "payments/withdrawals", element: <WithdrawalsPage /> },
        { path: "wheel/spins", element: <WheelSpinsPage /> },
        { path: "prizes", element: <PrizesPage /> },
        { path: "score", element: <ScorePage /> },
        { path: "score/conversions", element: <ConversionsPage /> },
        { path: "plans", element: <PlansPage /> },
        { path: "games", element: <GamesPage /> },
        { path: "faq", element: <FaqPage /> },
        { path: "notification", element: <NotificationPage /> },
      ],
    },
    {
      path: "sign-in",
      element: (
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      ),
    },
    {
      path: "404",
      element: <Page404 />,
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ];

  // Always call `useRoutes` here, regardless of loading state
  const routing = useRoutes(routes);

  // Show loading fallback if still loading authentication
  if (loading) {
    return renderFallback;
  }

  // Return routes after loading is complete
  return routing;
}
